import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';
import {
  diffShifts,
  hasShiftChanges,
  type EventShift,
  type ShiftDraft,
} from '@/utils/eventShifts';

/**
 * Supabase access for event shifts. See supabase/migrations/20260917180000_event_shifts.sql.
 */

interface ShiftRow {
  id: string;
  starts_at: string;
  ends_at: string;
  capacity: number | string | null;
  taken: number | string | null;
  mine: boolean | null;
}

/**
 * An event's shifts with how full each one is, in time order.
 *
 * Goes through `get_event_shifts` (SECURITY DEFINER) because someone deciding whether to
 * apply is not on the roster and cannot count `volunteer_shifts` rows themselves.
 * Returns null when the read failed, so callers can tell that apart from "no shifts".
 */
export async function fetchEventShifts(eventId: string): Promise<EventShift[] | null> {
  const { data, error } = await supabase.rpc('get_event_shifts', { p_event_id: eventId });
  if (error) {
    logger.error('get_event_shifts failed', { eventId, error });
    return null;
  }
  return ((data ?? []) as ShiftRow[]).map((row) => ({
    id: row.id,
    starts_at: row.starts_at,
    ends_at: row.ends_at,
    capacity: row.capacity == null ? null : Number(row.capacity),
    taken: Number(row.taken ?? 0),
    mine: row.mine === true,
  }));
}

/** Ids of the shifts one volunteer is on. Null when the read failed. */
export async function fetchVolunteerShiftIds(eventId: string, volunteerId: string): Promise<string[] | null> {
  const { data, error } = await supabase
    .from('volunteer_shifts')
    .select('shift_id')
    .eq('event_id', eventId)
    .eq('volunteer_id', volunteerId);
  if (error) {
    logger.error('volunteer_shifts read failed', { eventId, volunteerId, error });
    return null;
  }
  return (data ?? []).map((row: { shift_id: string }) => row.shift_id);
}

/** Who is on each shift of an event, as { shiftId: volunteerIds }. Null when the read failed. */
export async function fetchEventShiftRoster(eventId: string): Promise<Record<string, string[]> | null> {
  const { data, error } = await supabase
    .from('volunteer_shifts')
    .select('shift_id, volunteer_id')
    .eq('event_id', eventId);
  if (error) {
    logger.error('volunteer_shifts roster read failed', { eventId, error });
    return null;
  }
  const byShift: Record<string, string[]> = {};
  for (const row of (data ?? []) as { shift_id: string; volunteer_id: string }[]) {
    (byShift[row.shift_id] ??= []).push(row.volunteer_id);
  }
  return byShift;
}

/**
 * The signed-in volunteer takes themselves off a shift.
 *
 * `leave_shift` (SECURITY DEFINER) also tells the organizer, which the volunteer cannot do
 * from the client: the "Volunteers notify organization" policy only whitelists application,
 * membership and activity payloads. See 20260918120000_event_shift_notifications.sql.
 */
export async function leaveShift(shiftId: string): Promise<{ ok: true; left: boolean } | { ok: false; error: string }> {
  const { data, error } = await supabase.rpc('leave_shift', { p_shift_id: shiftId });
  if (error) {
    logger.error('leave_shift failed', { shiftId, error });
    return { ok: false, error: error.message || 'Failed to leave the shift. Please try again.' };
  }
  return { ok: true, left: (data as { left?: boolean } | null)?.left === true };
}

/**
 * The signed-in volunteer moves from one shift to another in a single step.
 *
 * `change_shift` drops the old shift and takes the new one in one transaction, so if the new
 * shift filled up meanwhile nothing changes and the volunteer keeps the one they had -- which
 * two separate calls could not promise. It also notifies the organization and its admins.
 * See 20260918150000_shift_changes.sql.
 */
export async function changeShift(
  fromShiftId: string,
  toShiftId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error } = await supabase.rpc('change_shift', {
    p_from_shift_id: fromShiftId,
    p_to_shift_id: toShiftId,
  });
  if (error) {
    logger.error('change_shift failed', { fromShiftId, toShiftId, error });
    return { ok: false, error: error.message || 'Could not change your shift. Please try again.' };
  }
  return { ok: true };
}

export type JoinShiftsResult =
  | { ok: true; added: string[]; full: string[]; started: string[] }
  | { ok: false; error: string };

/**
 * The signed-in volunteer, already on the event, takes one or more open shifts.
 * Like assignVolunteerShifts, shifts that filled up or started meanwhile come back in `full`
 * or `started` instead of failing the rest. See 20260918170000_volunteer_join_shifts.sql.
 */
export async function joinShifts(eventId: string, shiftIds: string[]): Promise<JoinShiftsResult> {
  if (shiftIds.length === 0) return { ok: true, added: [], full: [], started: [] };
  const { data, error } = await supabase.rpc('join_shifts', { p_event_id: eventId, p_shift_ids: shiftIds });
  if (error) {
    logger.error('join_shifts failed', { eventId, error });
    return { ok: false, error: error.message || 'Could not add you to those shifts. Please try again.' };
  }
  const row = (data ?? {}) as { added?: string[] | null; full?: string[] | null; started?: string[] | null };
  return { ok: true, added: row.added ?? [], full: row.full ?? [], started: row.started ?? [] };
}

export type AssignShiftsResult =
  | { ok: true; added: string[]; full: string[] }
  | { ok: false; error: string };

/**
 * Put an active volunteer on shifts. Capacity is enforced in the database: shifts that
 * were full come back in `full` instead of failing the whole call.
 */
export async function assignVolunteerShifts(
  eventId: string,
  volunteerId: string,
  shiftIds: string[],
): Promise<AssignShiftsResult> {
  if (shiftIds.length === 0) return { ok: true, added: [], full: [] };
  const { data, error } = await supabase.rpc('assign_volunteer_shifts', {
    p_event_id: eventId,
    p_volunteer_id: volunteerId,
    p_shift_ids: shiftIds,
  });
  if (error) {
    logger.error('assign_volunteer_shifts failed', { eventId, volunteerId, error });
    return { ok: false, error: error.message || 'Failed to assign shifts' };
  }
  const row = (data ?? {}) as { added?: string[] | null; full?: string[] | null };
  return { ok: true, added: row.added ?? [], full: row.full ?? [] };
}

export async function removeVolunteerShift(
  shiftId: string,
  volunteerId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error } = await supabase
    .from('volunteer_shifts')
    .delete()
    .eq('shift_id', shiftId)
    .eq('volunteer_id', volunteerId);
  if (error) {
    logger.error('volunteer_shifts delete failed', { shiftId, volunteerId, error });
    return { ok: false, error: error.message || 'Failed to remove shift' };
  }
  return { ok: true };
}

/**
 * Write the event form's shifts. Deletes run first so a shift removed and re-added with
 * the same times does not trip the (event_id, starts_at, ends_at) unique constraint.
 * Throws with a user-facing message on failure.
 */
export async function saveEventShifts(
  eventId: string,
  original: ShiftDraft[],
  current: ShiftDraft[],
): Promise<void> {
  const changes = diffShifts(original, current);
  if (!hasShiftChanges(changes)) return;

  if (changes.toDelete.length > 0) {
    const { error } = await supabase.from('event_shifts').delete().in('id', changes.toDelete);
    if (error) throw new Error(`Failed to remove shifts: ${error.message}`);
  }

  for (const { id, capacity } of changes.toUpdateCapacity) {
    const { error } = await supabase.from('event_shifts').update({ capacity }).eq('id', id);
    if (error) throw new Error(`Failed to update shift spots: ${error.message}`);
  }

  if (changes.toInsert.length > 0) {
    const { error } = await supabase.from('event_shifts').insert(
      changes.toInsert.map((d) => ({
        event_id: eventId,
        starts_at: d.starts_at,
        ends_at: d.ends_at,
        capacity: d.capacity,
      })),
    );
    if (error) throw new Error(`Failed to add shifts: ${error.message}`);
  }
}
